import {
  Accommodation,
  CreateAccommodation,
  CreateReservationService,
  ReservationDay,
  ReservationService,
} from "@/models";
import { Day } from "../-types";
import { LazyOperationEntity } from "@/models/commons";
import { v4 as uuid } from "uuid";

function deepCloneArray<T extends object>(arr: T[]): T[] {
  return arr.map((item) => ({ ...item }));
}

function noopReservationDay(
  reservationId: string,
  day: number,
): LazyOperationEntity<ReservationDay> {
  return {
    operation: "noop",
    value: {
      day: day,
      id: uuid(),
      reservationId: Number.parseInt(reservationId),
      name: "",
    },
  };
}

export class ScheduleManager {
  private listener: (days: Day[]) => void = () => {};
  days: Day[] = [];
  toDelete: Day[] = [];
  constructor(
    private readonly reservationId: string,
    accommodations: Accommodation[],
    services: ReservationService[],
    reservationDays: ReservationDay[],
  ) {
    this.initDays(accommodations, services, reservationDays);
  }

  listen(listener: (days: Day[]) => void) {
    this.listener = listener;
  }

  ignore() {
    this.listener = () => {};
  }

  handleAddDay() {
    const netDay: Day = {
      reservationDay: {
        operation: "create",
        value: {
          day: this.days.length + 1,
          id: uuid(),
          reservationId: Number.parseInt(this.reservationId),
          name: "",
        },
      },
      services: [],
      accommodations: [],
    };
    this.setDays([...this.days, netDay]);
  }

  handleDeleteDay(day: number) {
    let newDays = [...this.days];
    const d = newDays[day - 1];

    switch (d.reservationDay.operation) {
      case "create":
        d.reservationDay.operation = "noop";
        break;
      case "update":
      case null:
        d.reservationDay.operation = "delete";
        break;
    }

    d.services.forEach((s) => {
      switch (s.operation) {
        case "create":
          s.operation = "noop";
          break;
        case "update":
        case null:
          s.operation = "delete";
      }
    });

    d.accommodations.forEach((a) => {
      switch (a.operation) {
        case "create":
          a.operation = "noop";
          break;
        case "update":
        case null:
          a.operation = "delete";
      }
    });

    newDays = newDays.filter((d) => d.reservationDay.value.day !== day);

    for (let i = d.reservationDay.value.day - 1; i < newDays.length; i++) {
      const dy = newDays[i];
      dy.reservationDay.value.day = i + 1;
      if (dy.reservationDay.operation === null)
        dy.reservationDay.operation = "update";

      dy.services.forEach((s) => {
        s.value.day = i + 1;
        if (s.operation === null) s.operation = "update";
      });

      dy.accommodations.forEach((a) => {
        a.value.day = i + 1;
        if (a.operation === null) a.operation = "update";
      });
    }

    this.toDelete = [...this.toDelete, d];

    this.setDays(newDays);
  }

  handleReorderOfDays(newOrder: number[]) {
    const newDays: Day[] = [];
    for (const dayNumber of newOrder) {
      const day = this.days[dayNumber - 1]!;
      day.reservationDay.value.day = newDays.length + 1;
      if (day.reservationDay.operation === null)
        day.reservationDay.operation = "update";
      day.services.forEach((s) => {
        s.value.day = day.reservationDay.value.day;
        if (s.operation === null) s.operation = "update";
      });
      day.accommodations.forEach((a) => {
        a.value.day = day.reservationDay.value.day;
        if (a.operation === null) a.operation = "update";
      });
      newDays.push(day);
    }
    this.setDays(newDays);
  }

  handleDayNameChange(day: number, name: string) {
    const newDays = [...this.days];
    const d = newDays[day - 1];
    d.reservationDay.value.name = name;
    switch (d.reservationDay.operation) {
      case null:
        d.reservationDay.operation = "update";
        break;
      case "noop":
        d.reservationDay.operation = "create";
        break;
    }
    this.setDays(newDays);
  }

  handleAddService(day: number, service: CreateReservationService) {
    const newDays = [...this.days];
    const d = newDays[day - 1];
    d.services.push({
      operation: "create",
      value: {
        id: uuid(),
        ...service,
      },
    });
    this.setDays(newDays);
  }

  handleUpdateService(day: number, tourService: ReservationService) {
    const newDays = [...this.days];
    const s = newDays[day - 1]?.services.find(
      (s) => s.value.id === tourService.id,
    );
    if (!s) return;
    s.value = tourService;
    if (s.operation === null) s.operation = "update";
    this.setDays([...newDays]);
  }

  handleReorderOfServices(dayNumber: number, newOrder: number[]) {
    const newDays = [...this.days];
    const day = newDays[dayNumber - 1];

    if (!day || day.services.length !== newOrder.length) return;

    day.services = newOrder
      .map((num) => day.services[num - 1])
      .map((d, i) => {
        d.value.order = i + 1;
        if (d.operation === null) d.operation = "update";
        return d;
      });
    this.setDays(newDays);
  }

  handleDeleteService(day: number, tourServiceId: string) {
    const newDays = [...this.days];
    const sid = newDays[day - 1]?.services.findIndex(
      (s) => s.value.id === tourServiceId,
    );
    if (sid < 0) return;
    const s = newDays[day - 1]?.services[sid];
    if (s.operation !== "create") s.operation = "delete";
    else s.operation = "noop";

    this.toDelete = [
      ...this.toDelete,
      {
        reservationDay: noopReservationDay(this.reservationId, -1),
        accommodations: [],
        services: [s],
      },
    ];
    newDays[day - 1].services = newDays[day - 1].services
      .filter((s) => s.value.id !== tourServiceId)
      .map((s, i) => {
        const mustUpdate = s.value.order !== i + 1;
        s.value.order = i + 1;
        if (s.operation === null && mustUpdate) s.operation = "update";
        return s;
      });
    this.setDays(newDays);
  }

  handleNewAccommodation(day: number, accommodation: CreateAccommodation) {
    const newDays = [...this.days];
    const d = newDays[day - 1];
    d.accommodations.push({
      operation: "create",
      value: {
        id: uuid(),
        ...accommodation,
      },
    });
    this.setDays(newDays);
  }

  handleReorderAccommodations() {}

  handleUpdateAccommodation(day: number, accommodation: Accommodation) {
    const newDays = [...this.days];
    const a = newDays[day - 1]?.accommodations.find(
      (a) => a.value.id === accommodation.id,
    );
    if (!a) return;
    a.value = accommodation;
    if (a.operation === null) a.operation = "update";
    this.setDays(newDays);
  }

  handleDeleteAccommodation(day: number, accommodationId: string) {
    const newDays = [...this.days];
    const sid = newDays[day - 1]?.accommodations.findIndex(
      (s) => s.value.id === accommodationId,
    );
    if (sid < 0) return;
    const s = newDays[day - 1]?.accommodations[sid];
    if (s.operation !== "create") s.operation = "delete";
    else s.operation = "noop";
    this.toDelete = [
      ...this.toDelete,
      {
        reservationDay: noopReservationDay(this.reservationId, -1),
        accommodations: [s],
        services: [],
      },
    ];

    newDays[day - 1].accommodations = newDays[day - 1].accommodations.filter(
      (s) => s.value.id !== accommodationId,
    );
    this.setDays(newDays);
  }

  private setDays(days: Day[]) {
    this.days = days;
    this.listener(this.days);
  }
  private initDays(
    accommodations: Accommodation[],
    services: ReservationService[],
    reservationDays: ReservationDay[],
  ) {
    for (const reservationService of deepCloneArray(services)) {
      const day = this.days[reservationService.day - 1];
      const serviceLazy = {
        operation: null,
        value: reservationService,
      };
      if (day) {
        day.services.push(serviceLazy);
      } else {
        this.days[reservationService.day - 1] = {
          services: [serviceLazy],
          reservationDay: noopReservationDay(
            this.reservationId,
            reservationService.day,
          ),
          accommodations: [],
        };
      }
    }

    for (const rd of deepCloneArray(reservationDays)) {
      const day = this.days[rd.day - 1];
      if (day) {
        day.reservationDay = {
          operation: null,
          value: rd,
        };
      } else
        this.days[rd.day - 1] = {
          services: [],
          accommodations: [],
          reservationDay: {
            operation: null,
            value: rd,
          },
        };
    }

    for (const acc of deepCloneArray(accommodations)) {
      const day = this.days[acc.day - 1];
      const lazyAcc = {
        operation: null,
        value: acc,
      };
      if (day) {
        day.accommodations.push(lazyAcc);
      } else {
        this.days[acc.day - 1] = {
          services: [],
          accommodations: [lazyAcc],
          reservationDay: noopReservationDay(this.reservationId, acc.day),
        };
      }
    }
    for (let i = 0; i < this.days.length; i++) {
      if (!this.days[i]) {
        this.days[i] = {
          services: [],
          accommodations: [],
          reservationDay: noopReservationDay(this.reservationId, i + 1),
        };
      }

      this.days[i].services = this.days[i].services.sort(
        (a, b) => (a.value.order ?? 0) - (b.value.order ?? 0),
      );
    }

    for (const day of this.days) {
      day.services.forEach((s, i) => {
        if (s.value.order === i + 1) return;
        s.value.order = i + 1;
        s.operation = "update";
      });
    }
  }
}
